import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  Datagrid,
  DatagridColumn,
  DatagridFormat,
  DatagridSortDirection,
} from '@zipari/shared-ds-util-datagrid';
import { hasOwnNestedProperty } from '../../../shared/utils/hasOwnNestedProperty';
import { DatagridDigitalDataService } from './../datagrid-digital-data.service';

@Component({
  selector: 'datagrid-header',
  templateUrl: './datagrid-header.component.html',
  styleUrl: './datagrid-header.component.scss',
})
export class DatagridHeaderComponent implements OnChanges {
  @Input() config: Datagrid;
  @Input() data: object[];
  @Input() sortOrder: string; // which property is the grid sorted by
  @Input() selectedRows: object[] = [];
  @Output() sortHeaderClicked: EventEmitter<any> = new EventEmitter();
  @Output() selectedRowsChange = new EventEmitter<object[]>();

  defaultRows: object[];
  formats = DatagridFormat;
  rows: object[];
  sortDirections = DatagridSortDirection;
  public selectedRowsTitle = 'Rows Selected';

  constructor(public digitalDataGridService: DatagridDigitalDataService) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.data) this.rows = this.getData();
    this.hideUnusedColumns();
  }

  public get rowsSelectedStatus(): string {
    return this.config.selectedRowsTitle || this.selectedRowsTitle;
  }

  get selectIcon(): string {
    return this.areAllRowsSelected()
      ? 'checkbox_active'
      : this.selectedRows.length > 0
        ? 'checkbox_all'
        : 'checkbox';
  }

  // Toggle the selection of rows
  toggleAllRows() {
    this.selectedRows = this.selectedRows.length !== 0 ? [] : [...this.rows];
    this.selectedRowsChange.emit(this.selectedRows);
  }

  isRowSelected(row) {
    return this.selectedRows.includes(row);
  }

  areAllRowsSelected() {
    return this.rows?.every((row) => this.isRowSelected(row));
  }

  getColumnWidth(column: DatagridColumn) {
    return column.width && this.rows.length > 0 && column.width;
  }

  showHeaderLabel(column: any, formats: any, rows: any): boolean {
    // check if the column should show it's label based on it's format.
    // currently all column formats except ACTION_ICONS show their label by default
    // only show a ACTION_ICONS formatted col's label if the showActionIconsLabel flag is true
    const showFormatsLabel =
      column.format === formats.ACTION_ICONS
        ? column.showActionIconsLabel || false
        : true;

    return (
      showFormatsLabel && (0 < rows?.length || this.config?.noData?.showHeader)
    );
  }

  handleColumnSortByClick(event: Event, column: DatagridColumn) {
    this.sortHeaderClicked.emit({ event, column });
  }

  getData() {
    return this.data
      ? [...(typeof this.data === 'string' ? JSON.parse(this.data) : this.data)]
      : [];
  }

  hideUnusedColumns() {
    if (this.config.hideUnusedColumns && this.rows?.length > 0) {
      let newColumns = [];
      /* eslint-disable-next-line */

      this.rows.forEach((dataRow: any) => {
        const columns = this.config.columns.filter((column) =>
          hasOwnNestedProperty(dataRow, column.prop),
        );

        newColumns = [
          ...newColumns,
          ...columns.filter((column) => newColumns.indexOf(column) < 0),
        ];
      });
      this.config = { ...this.config, columns: newColumns };
    }
  }

  /**
   * Gets type of value (it can be of type: string, number, object etc)
   * @param {any} value
   * @returns type of value
   */
  getTypeOf(value: any): any {
    return typeof value;
  }
}
